import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { ReminderButtons } from "@/components/MessagingChannelsPanel";
import { Scissors, Plus, Trash2, AlertTriangle, FileDown, FileSignature, Upload, X as XIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Programma Chirurgia + Gestisci Consensi (firma grafometrica).
 * Collegato a paziente e visita. Avviso rosso se consenso richiesto non firmato.
 */
const STATUSES = [
  ["bozza", "Bozza"], ["programmato", "Programmato"], ["consenso_da_firmare", "Consenso da firmare"],
  ["pronto", "Pronto"], ["annullato", "Annullato"], ["eseguito", "Eseguito"],
];
const statusColor = (s) => ({
  bozza: "bg-slate-100 text-slate-600", programmato: "bg-brand-azure/15 text-brand-azure",
  consenso_da_firmare: "bg-amber-100 text-amber-700", pronto: "bg-emerald-100 text-emerald-700",
  annullato: "bg-slate-200 text-slate-500", eseguito: "bg-brand-navy text-white",
}[s] || "bg-slate-100 text-slate-600");

export default function SurgeryProgramBlock({ visitId, patient, phone, email, sites = [], readOnly }) {
  const [procedures, setProcedures] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [signedConsents, setSignedConsents] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ procedure_key: "", eye: "OD", planned_date: "", planned_time: "", sede: "", chirurgo: "", anestesia: "", priorita: "normale", note: "", esami_preop: "" });
  const [signTarget, setSignTarget] = useState(null); // program
  const [signature, setSignature] = useState(null);
  const [signBusy, setSignBusy] = useState(false);
  const [consentsOpen, setConsentsOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState(null); // {procedure_key,title,version,source_url,pdf_b64,pdf_name}
  const [overrideFor, setOverrideFor] = useState(null); // {program, motivo}

  const pParams = {
    fiscal_code: patient?.fiscal_code || undefined, name: patient?.name || undefined,
    surname: patient?.surname || undefined, birth: patient?.birth || undefined,
  };

  const load = useCallback(async () => {
    try {
      const [{ data: procs }, { data: progs }, { data: tpls }, { data: signed }] = await Promise.all([
        api.get("/medico/surgery/procedures"),
        api.get("/medico/surgery/programs", { params: pParams }),
        api.get("/medico/consent-templates"),
        api.get("/medico/surgery/consents", { params: pParams }),
      ]);
      setProcedures(procs || []); setPrograms(progs || []); setTemplates(tpls || []); setSignedConsents(signed || []);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.fiscal_code, patient?.name, patient?.surname]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.procedure_key) { toast.error("Seleziona la procedura"); return; }
    try {
      await api.post("/medico/surgery/programs", {
        ...form, visit_id: visitId,
        patient_fiscal_code: patient?.fiscal_code, patient_name: patient?.name,
        patient_surname: patient?.surname, patient_birth: patient?.birth,
        status: "programmato",
      });
      toast.success("Intervento programmato");
      setFormOpen(false);
      setForm({ procedure_key: "", eye: "OD", planned_date: "", planned_time: "", sede: "", chirurgo: "", anestesia: "", priorita: "normale", note: "", esami_preop: "" });
      load();
    } catch (e) { toast.error(apiErr(e, "Errore creazione programma")); }
  };

  const patchProgram = async (p, patch) => {
    try {
      await api.patch(`/medico/surgery/programs/${p.id}`, patch);
      load();
    } catch (e) {
      const msg = apiErr(e, "Errore aggiornamento");
      if (String(msg).includes("Consenso informato non firmato")) setOverrideFor({ program: p, target: patch.status, motivo: "" });
      else toast.error(msg);
    }
  };

  const delProgram = async (p) => {
    if (!window.confirm(`Eliminare il programma "${p.procedure_label}"?`)) return;
    try { await api.delete(`/medico/surgery/programs/${p.id}`); load(); } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const sign = async () => {
    if (!signature) { toast.error("Firma mancante: firma nel riquadro"); return; }
    setSignBusy(true);
    try {
      const tpl = templates.find((t) => t.procedure_key === signTarget.procedure_key);
      await api.post("/medico/surgery/consents/sign", {
        program_id: signTarget.id, procedure_key: signTarget.procedure_key,
        template_id: tpl?.id || null, signature_png: signature,
        patient_fiscal_code: patient?.fiscal_code, patient_name: patient?.name,
        patient_surname: patient?.surname, patient_birth: patient?.birth,
      });
      toast.success("Consenso firmato e archiviato");
      setSignTarget(null); setSignature(null);
      load();
    } catch (e) { toast.error(apiErr(e, "Errore firma consenso")); }
    finally { setSignBusy(false); }
  };

  const downloadSigned = async (c) => {
    try {
      const r = await api.get(`/medico/surgery/consents/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `consenso_${c.procedure_key}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e, "Errore download")); }
  };

  const onPdfFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("PDF troppo grande (max 6MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setUploadForm((u) => ({ ...u, pdf_b64: String(reader.result).split(",")[1], pdf_name: f.name }));
    reader.readAsDataURL(f);
  };

  const uploadTemplate = async () => {
    if (!uploadForm?.procedure_key || !uploadForm?.title) { toast.error("Procedura e titolo obbligatori"); return; }
    try {
      await api.post("/medico/consent-templates", uploadForm);
      toast.success("Consenso ufficiale caricato");
      setUploadForm(null);
      load();
    } catch (e) { toast.error(apiErr(e, "Errore upload")); }
  };

  const byCat = procedures.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});
  const unsigned = programs.filter((p) => !p.consenso_firmato && !["annullato", "bozza"].includes(p.status));

  return (
    <div data-testid="surgery-program-block">
      {unsigned.length > 0 && (
        <div className="bg-red-50 border-2 border-red-400 rounded-xl p-3 mb-3 flex items-start gap-2" data-testid="consent-missing-alert" role="alert">
          <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-red-800">
            <b>Consenso non firmato</b> per: {unsigned.map((p) => `${p.procedure_label} (${p.eye})`).join(", ")}.
            Il consenso informato è richiesto prima dell&apos;intervento.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Scissors size={16} className="text-brand-navy" />
          <span className="text-sm font-extrabold text-brand-navy">Interventi programmati ({programs.length})</span>
        </div>
        <div className="flex gap-2">
          <button data-testid="surgery-consents-open" onClick={() => setConsentsOpen(true)}
            className="text-xs font-bold text-brand-azure border border-brand-azure/40 hover:bg-brand-azure/10 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
            <ShieldCheck size={12} /> Gestisci consensi
          </button>
          {!readOnly && (
            <button data-testid="surgery-add-open" onClick={() => setFormOpen((x) => !x)}
              className="bg-brand-navy hover:bg-brand-navyDark text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5">
              <Plus size={13} /> Programma intervento
            </button>
          )}
        </div>
      </div>

      {formOpen && (
        <div className="border border-brand-navy/20 bg-brand-navy/5 rounded-xl p-3 mb-3" data-testid="surgery-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="col-span-2">
              <label className="text-[10px] font-bold uppercase text-slate-500">Procedura *</label>
              <select data-testid="surgery-procedure" value={form.procedure_key} onChange={(e) => setForm({ ...form, procedure_key: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                <option value="">Seleziona…</option>
                {Object.entries(byCat).map(([cat, items]) => (
                  <optgroup key={cat} label={cat}>
                    {items.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Occhio</label>
              <select data-testid="surgery-eye" value={form.eye} onChange={(e) => setForm({ ...form, eye: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                <option>OD</option><option>OS</option><option>OU</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Priorità</label>
              <select data-testid="surgery-priorita" value={form.priorita} onChange={(e) => setForm({ ...form, priorita: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                <option value="normale">Normale</option><option value="alta">Alta</option><option value="urgente">Urgente</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Data prevista</label>
              <input type="date" data-testid="surgery-date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Orario</label>
              <input type="time" data-testid="surgery-time" value={form.planned_time} onChange={(e) => setForm({ ...form, planned_time: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Sede</label>
              {sites.length ? (
                <select value={form.sede} onChange={(e) => setForm({ ...form, sede: e.target.value })} data-testid="surgery-sede" className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                  <option value="">—</option>
                  {sites.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              ) : (
                <input value={form.sede} onChange={(e) => setForm({ ...form, sede: e.target.value })} data-testid="surgery-sede-input" className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
              )}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Chirurgo</label>
              <input data-testid="surgery-chirurgo" value={form.chirurgo} onChange={(e) => setForm({ ...form, chirurgo: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Anestesia</label>
              <input data-testid="surgery-anestesia" value={form.anestesia} onChange={(e) => setForm({ ...form, anestesia: e.target.value })} placeholder="topica / peribulbare…" className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="text-[10px] font-bold uppercase text-slate-500">Esami preoperatori richiesti</label>
              <input data-testid="surgery-preop" value={form.esami_preop} onChange={(e) => setForm({ ...form, esami_preop: e.target.value })} placeholder="es. biometria, ECG, esami ematici…" className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs" />
            </div>
            <div className="col-span-2 md:col-span-4">
              <label className="text-[10px] font-bold uppercase text-slate-500">Note cliniche</label>
              <textarea data-testid="surgery-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs resize-none" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setFormOpen(false)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100">Annulla</button>
            <button data-testid="surgery-create" onClick={create} className="px-4 py-1.5 rounded-lg text-xs font-bold bg-brand-navy text-white">Programma</button>
          </div>
        </div>
      )}

      {programs.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 text-center border-2 border-dashed border-[#E2E8F0] rounded-lg" data-testid="surgery-empty">
          Nessun intervento programmato per questo paziente.
        </div>
      ) : (
        <div className="space-y-2">
          {programs.map((p) => {
            const tpl = templates.find((t) => t.procedure_key === p.procedure_key);
            return (
              <div key={p.id} className={`border rounded-lg p-3 ${!p.consenso_firmato && !["annullato", "bozza"].includes(p.status) ? "border-red-300 bg-red-50/40" : "border-[#E2E8F0]"}`} data-testid={`surgery-program-${p.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-extrabold text-sm text-brand-navy">{p.procedure_label}</span>
                  <span className="text-[10px] font-bold bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 rounded">{p.eye}</span>
                  {p.priorita !== "normale" && <span className="text-[10px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded uppercase">{p.priorita}</span>}
                  <select disabled={readOnly} value={p.status} data-testid={`surgery-status-${p.id}`}
                    onChange={(e) => patchProgram(p, { status: e.target.value })}
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded border-0 ${statusColor(p.status)}`}>
                    {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {p.consenso_firmato ? (
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded flex items-center gap-1"><ShieldCheck size={10} /> Consenso firmato</span>
                  ) : (
                    <span className="text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded flex items-center gap-1" data-testid={`surgery-consent-missing-${p.id}`}><AlertTriangle size={10} /> Consenso non firmato</span>
                  )}
                  <div className="ml-auto flex items-center gap-1.5">
                    {!readOnly && !p.consenso_firmato && (
                      <button data-testid={`surgery-sign-${p.id}`} onClick={() => { setSignTarget(p); setSignature(null); }}
                        className="text-[11px] font-bold text-white bg-brand-orange hover:bg-brand-orangeDark px-2.5 py-1 rounded-lg flex items-center gap-1">
                        <FileSignature size={11} /> Firma consenso
                      </button>
                    )}
                    {!readOnly && <button onClick={() => delProgram(p)} data-testid={`surgery-del-${p.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={13} /></button>}
                  </div>
                </div>
                <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-3">
                  {p.planned_date && <span>📅 {p.planned_date}{p.planned_time ? ` ${p.planned_time}` : ""}</span>}
                  {p.sede && <span>Sede: {p.sede}</span>}
                  {p.chirurgo && <span>Chirurgo: {p.chirurgo}</span>}
                  {p.anestesia && <span>Anestesia: {p.anestesia}</span>}
                </div>
                {p.esami_preop && <div className="text-[11px] text-slate-600 mt-0.5">Preop: {p.esami_preop}</div>}
                {p.note && <div className="text-[11px] text-slate-500 mt-0.5 italic">{p.note}</div>}
                {p.override_consenso && <div className="text-[10px] text-amber-700 mt-1">⚠ Override consenso: {p.override_motivo || "motivazione registrata"}</div>}
                {!readOnly && !["annullato", "eseguito"].includes(p.status) && (
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Promemoria:</span>
                    <ReminderButtons
                      patient={patient} phone={phone} email={email} compact
                      eventType={p.consenso_firmato ? "surgery" : "consent_reminder"}
                      context={{ data: p.planned_date, ora: p.planned_time, sede: p.sede, medico: p.chirurgo, procedura: p.procedure_label }}
                    />
                  </div>
                )}
                {tpl && !tpl.has_pdf && !p.consenso_firmato && (
                  <div className="text-[10px] text-amber-600 mt-1">Il PDF ufficiale del consenso per questa procedura non è ancora caricato (Gestisci consensi).</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Consensi firmati archiviati */}
      {signedConsents.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2" data-testid="signed-consents-list">
          <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1.5">Consensi firmati nel fascicolo ({signedConsents.length})</div>
          <div className="space-y-1">
            {signedConsents.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-[11px] text-slate-700 bg-emerald-50/50 border border-emerald-100 rounded px-2 py-1.5">
                <ShieldCheck size={11} className="text-emerald-600" />
                <span className="font-bold">{c.template_title || c.procedure_key}</span>
                <span className="text-slate-500">{new Date(c.signed_at).toLocaleString("it-IT")}</span>
                <button data-testid={`consent-download-${c.id}`} onClick={() => downloadSigned(c)} className="ml-auto text-brand-azure hover:underline font-bold flex items-center gap-1"><FileDown size={11} /> PDF firmato</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODAL FIRMA GRAFOMETRICA */}
      {signTarget && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !signBusy && setSignTarget(null)}>
          <div className="bg-white rounded-xl p-5 max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="consent-sign-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold text-brand-navy">Firma consenso informato</h3>
              <button onClick={() => setSignTarget(null)}><XIcon size={18} /></button>
            </div>
            <div className="text-xs text-slate-600 mb-2">
              <b>{signTarget.procedure_label}</b> ({signTarget.eye}) — Paziente: <b>{patient?.name} {patient?.surname}</b>
            </div>
            {(() => {
              const tpl = templates.find((t) => t.procedure_key === signTarget.procedure_key);
              return tpl ? (
                <div className="text-[11px] bg-slate-50 border border-[#E2E8F0] rounded-lg p-2 mb-2">
                  Documento: <b>{tpl.title}</b> · v.{tpl.version}{tpl.source_url ? <> · fonte: <a href={tpl.source_url} target="_blank" rel="noreferrer" className="text-brand-azure underline">{tpl.source_url}</a></> : ""}
                  {tpl.has_pdf ? (
                    <button onClick={async () => {
                      try {
                        const r = await api.get(`/medico/consent-templates/${tpl.id}/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.click();
                      } catch { toast.error("Errore apertura PDF"); }
                    }} className="ml-2 text-brand-azure font-bold underline">Apri PDF ufficiale</button>
                  ) : <span className="text-amber-600 font-bold ml-2">PDF ufficiale non ancora caricato</span>}
                </div>
              ) : (
                <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2 text-amber-800">
                  Nessun consenso ufficiale mappato per questa procedura. Carica il PDF SISO da &quot;Gestisci consensi&quot;. La firma verrà comunque registrata con attestazione.
                </div>
              );
            })()}
            <div className="text-[11px] text-slate-500 mb-1">Il paziente firma nel riquadro (mouse o touch):</div>
            <SignaturePad onChange={setSignature} dataTestid="consent-signature-canvas" />
            <div className="flex justify-end gap-2 mt-3">
              <button disabled={signBusy} onClick={() => setSignTarget(null)} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Annulla</button>
              <button data-testid="consent-sign-confirm" disabled={signBusy || !signature} onClick={sign}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-brand-navy text-white disabled:opacity-50">
                {signBusy ? "Registrazione…" : "Registra firma"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL OVERRIDE CONSENSO */}
      {overrideFor && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full" data-testid="consent-override-modal">
            <p className="text-sm font-semibold text-brand-navy">Consenso non firmato. Per marcare come &quot;pronto&quot; serve una motivazione esplicita (override).</p>
            <textarea rows={2} value={overrideFor.motivo} onChange={(e) => setOverrideFor({ ...overrideFor, motivo: e.target.value })}
              placeholder="Motivazione override…" data-testid="consent-override-motivo"
              className="mt-2 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setOverrideFor(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100">Annulla</button>
              <button data-testid="consent-override-confirm" disabled={!overrideFor.motivo.trim()}
                onClick={() => { patchProgram(overrideFor.program, { status: overrideFor.target, override_consenso: true, override_motivo: overrideFor.motivo }); setOverrideFor(null); }}
                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-red-600 text-white disabled:opacity-50">Conferma override</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL GESTISCI CONSENSI */}
      {consentsOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setConsentsOpen(false); setUploadForm(null); }}>
          <div className="bg-white rounded-xl p-5 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="consents-manage-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold text-brand-navy">Gestisci consensi informati</h3>
              <button onClick={() => { setConsentsOpen(false); setUploadForm(null); }}><XIcon size={18} /></button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Usa solo consensi informati ufficiali (SISO / società scientifiche). Carica il PDF originale e mappalo alla procedura: il testo medico-legale non viene mai generato automaticamente.
            </p>
            <div className="max-h-64 overflow-y-auto space-y-1.5 mb-3">
              {procedures.map((proc) => {
                const tpl = templates.find((t) => t.procedure_key === proc.key);
                return (
                  <div key={proc.key} className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3 py-2 text-xs">
                    <span className="font-bold text-brand-navy flex-1">{proc.label}</span>
                    {tpl ? (
                      <>
                        <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${tpl.has_pdf ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {tpl.has_pdf ? `PDF v.${tpl.version}` : "Manca PDF"}
                        </span>
                        {tpl.has_pdf && (
                          <button onClick={async () => {
                            try {
                              const r = await api.get(`/medico/consent-templates/${tpl.id}/pdf`, { responseType: "blob" });
                              const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = tpl.pdf_name || "consenso.pdf"; a.click();
                            } catch { toast.error("Errore download"); }
                          }} className="text-brand-azure font-bold underline">Scarica</button>
                        )}
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400">Nessun consenso mappato</span>
                    )}
                    <button data-testid={`consent-upload-${proc.key}`}
                      onClick={() => setUploadForm({ procedure_key: proc.key, title: `Consenso informato — ${proc.label}`, version: "", source_url: "", pdf_b64: null, pdf_name: null })}
                      className="text-white bg-brand-azure hover:bg-brand-navy px-2 py-1 rounded font-bold flex items-center gap-1"><Upload size={10} /> Carica PDF</button>
                  </div>
                );
              })}
            </div>
            {uploadForm && (
              <div className="border-2 border-brand-azure/30 rounded-lg p-3 bg-brand-azure/5" data-testid="consent-upload-form">
                <div className="grid grid-cols-2 gap-2">
                  <input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} placeholder="Titolo documento" data-testid="consent-upload-title" className="col-span-2 px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                  <input value={uploadForm.version} onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })} placeholder="Versione/data (es. 2024-03)" data-testid="consent-upload-version" className="px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                  <input value={uploadForm.source_url} onChange={(e) => setUploadForm({ ...uploadForm, source_url: e.target.value })} placeholder="Fonte URL (sito SISO)" data-testid="consent-upload-source" className="px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs" />
                  <label className="col-span-2 text-xs font-bold text-brand-azure cursor-pointer flex items-center gap-1.5">
                    <Upload size={12} /> {uploadForm.pdf_name || "Seleziona PDF ufficiale…"}
                    <input type="file" accept="application/pdf" className="hidden" data-testid="consent-upload-file" onChange={onPdfFile} />
                  </label>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <button onClick={() => setUploadForm(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600">Annulla</button>
                  <button data-testid="consent-upload-save" onClick={uploadTemplate} className="px-4 py-1.5 rounded-lg text-xs font-bold bg-brand-navy text-white">Salva consenso</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
